(function () {
  'use strict';

  var COOKIE_CHOICE = 'hilo_geo_choice';
  var COOKIE_DISMISSED = 'hilo_geo_banner_dismissed';
  var COOKIE_DAYS = 400;
  // Session cookie (no expiry) shared by both stores: set before a first-visit auto-redirect and when a
  // visitor arrives from the other store, so nobody is auto-redirected more than once per session.
  var COOKIE_REDIRECT_GUARD = 'hilo_geo_redirect_guard';
  var SUGGESTIONS_URL = '/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true';
  // Upper bound for reading a translated page's English twin when looking up its cross-store URL.
  var ALTERNATE_FETCH_TIMEOUT = 3000;
  // Query parameters that only mean something on the store that issued them (theme preview, QA hooks).
  var LOCAL_PARAMS = /^(?:preview_theme_id|_ab|_bt|_fd|_sc|hilo_geo_[a-z_]+)$/i;
  // Never moved off these paths, whatever template renders them: cart, checkout, account, and the
  // four Shopify app-proxy prefixes (apps, a, community, tools).
  var NO_REDIRECT_PATH = /^\/(?:[a-z]{2}-[a-z0-9]+\/)?(?:cart|checkouts?|account|orders|apps|a|community|tools|challenge|password)(?:\/|$)/i;
  // Crawlers and Google's testing tools identify themselves in the user agent. Several do not say
  // "bot": Search Console's URL Inspection and the Rich Results Test send Google-InspectionTool,
  // PageSpeed Insights sends Chrome-Lighthouse. They must see exactly what a visitor of this store sees.
  // Pinterest and Quora are matched by their crawler names only: the bare app names also appear in
  // the in-app browsers real shoppers use.
  var BOT_PATTERN = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|embedly|quora link preview|vkshare|w3c_validator|yandex|google-inspectiontool|googleother|google-read-aloud|google-pagerenderer|google-site-verification|google web preview|google favicon|googleweblight|mediapartners-google|apis-google|feedfetcher-google|chrome-lighthouse|ptst|gtmetrix/i;

  var TOKEN_CURRENT = '[CURRENT MARKET]';
  var TOKEN_DETECTED = '[DETECTED COUNTRY]';
  var TOKEN_RECOMMENDED = '[RECOMMENDED MARKET]';

  // Share the preference across hilo.com + us.hilo.com (Phase 2 cross-store routing) by scoping
  // to the registrable apex; fall back to a host-only cookie on dev / *.myshopify.com.
  function cookieDomain() {
    var host = (window.location.hostname || '').toLowerCase();
    if (host === 'hilo.com' || /\.hilo\.com$/.test(host)) return '.hilo.com';
    return null;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    var domain = cookieDomain();
    document.cookie =
      name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax' + (domain ? '; domain=' + domain : '');
  }

  function eraseCookie(name) {
    var domain = cookieDomain();
    var expired = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = expired;
    if (domain) document.cookie = expired + '; domain=' + domain;
  }

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split(';') : [];
    for (var i = 0; i < parts.length; i++) {
      var pair = parts[i].split('=');
      if (pair[0].trim() === name) {
        try {
          return decodeURIComponent(pair.slice(1).join('=').trim());
        } catch (error) {
          eraseCookie(name);
          return null;
        }
      }
    }
    return null;
  }

  function parseList(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(function (item) {
        return item.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function shortLocale(value) {
    return (value || '').toString().trim().toLowerCase().split('-')[0];
  }

  // Country names in the visitor's chosen popup language. Browsers ship the CLDR region names behind
  // Intl.DisplayNames, so the merchant types each country once and the popup still reads
  // "Deutschland" in German and "Allemagne" in French. The merchant-typed name is the fallback for
  // browsers without the API (Safari < 14.1) and for codes the browser does not know.
  var regionNamesCache = {};
  function countryDisplayName(code, locale, fallback) {
    code = (code || '').toString().trim().toUpperCase();
    locale = shortLocale(locale);
    fallback = fallback || code;
    if (!/^[A-Z]{2}$/.test(code) || !locale) return fallback;
    if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return fallback;
    try {
      var names = regionNamesCache[locale];
      if (!names) {
        names = regionNamesCache[locale] = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' });
      }
      return names.of(code) || fallback;
    } catch (error) {
      return fallback;
    }
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  // Gate for the QA hooks below. A query parameter alone cannot enable them on the live theme;
  // Shopify must report a preview theme role.
  function isPreviewOrLocal() {
    var host = (window.location.hostname || '').toLowerCase();
    var isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
    var themeRole = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
    return isLocal || themeRole === 'unpublished' || themeRole === 'development';
  }

  // QA hook for local development and unpublished Shopify previews only. Example:
  // ?hilo_geo_country=FR&hilo_geo_language=fr&hilo_geo_name=France
  function debugDetectionFromUrl() {
    if (!isPreviewOrLocal()) return null;
    if (!window.URLSearchParams) return null;

    var params = new URLSearchParams(window.location.search || '');
    var country = (params.get('hilo_geo_country') || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) return null;
    var locale = shortLocale(params.get('hilo_geo_language') || '');
    var name = (params.get('hilo_geo_name') || '').trim();

    return {
      detected_values: {
        country: { handle: country, name: name },
        language: locale ? { handle: locale } : null
      }
    };
  }

  // QA hook for the auto-redirect, behind the same preview/local gate. Unlike hilo_geo_country it
  // replaces only Shopify's country lookup: cookies, referrer, session and page rules all still apply,
  // so the real flow can be tested from outside the US. ?hilo_geo_simulate=US lasts for the tab,
  // ?hilo_geo_simulate=off ends it.
  function simulatedDetection() {
    if (!isPreviewOrLocal() || !window.URLSearchParams) return null;
    var country = '';
    try {
      var params = new URLSearchParams(window.location.search || '');
      if (params.has('hilo_geo_simulate')) {
        country = (params.get('hilo_geo_simulate') || '').trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(country)) {
          window.sessionStorage.setItem('hilo_geo_simulate', country);
        } else {
          window.sessionStorage.removeItem('hilo_geo_simulate');
        }
      }
      country = window.sessionStorage.getItem('hilo_geo_simulate') || '';
    } catch (error) {
      return null;
    }
    if (!/^[A-Z]{2}$/.test(country)) return null;
    return { detected_values: { country: { handle: country, name: '' } } };
  }

  function isBot() {
    return BOT_PATTERN.test(navigator.userAgent || '');
  }

  function isAllowedDestination(url) {
    if (!url || !url.hostname) return false;
    var host = url.hostname.toLowerCase();
    if (url.protocol === 'https:' && (host === 'hilo.com' || host === 'www.hilo.com' || host === 'us.hilo.com')) {
      return true;
    }
    // Local theme development uses HTTP and a host-only cookie; never extend this exception to a
    // different hostname.
    return host === window.location.hostname && (host === 'localhost' || host === '127.0.0.1');
  }

  // Parse the "Market URL subfolders" setting into an ISO -> subfolder lookup. Each line is
  // "<subfolder> | <comma separated ISO codes>", e.g. "eu | AT, DE, ES". Shopify serves each market
  // under a <locale>-<subfolder> path (hilo.com/de-eu), so a bare locale prefix is not a valid URL.
  function parseSubfolders(value) {
    var map = {};
    if (!value) return map;
    var lines = value.split(/[\r\n]+/);
    for (var i = 0; i < lines.length; i++) {
      var parts = lines[i].split('|');
      if (parts.length < 2) continue;
      var subfolder = parts[0].trim().toLowerCase();
      if (!subfolder) continue;
      var codes = parseList(parts[1]);
      for (var j = 0; j < codes.length; j++) {
        map[codes[j]] = subfolder;
      }
    }
    return map;
  }

  // Build the URL for a store in a given language. Shopify publishes each market/locale pair under a
  // "<locale>-<market>" subfolder, so the subfolder is required: hilo.com/de alone is not a published
  // path and 302s back to the primary market, losing both the market and the language. With no
  // subfolder configured the URL is left alone - a single-market store such as us.hilo.com serves its
  // primary locale at the root, and guessing a bare "/<locale>" would recreate that same broken URL.
  function withLocalePrefix(urlStr, locale, subfolder) {
    if (!subfolder) return urlStr;
    var segment = (locale || 'en') + '-' + subfolder;
    try {
      var url = new URL(urlStr);
      var path = url.pathname.replace(/^\/+/, '').replace(/\/+$/, '');
      url.pathname = '/' + segment + (path ? '/' + path : '');
      return url.toString();
    } catch (error) {
      return urlStr;
    }
  }

  // Split a "<locale>-<market>" subfolder off a path: /de-eu/pages/unternehmen -> de-eu + /pages/unternehmen.
  function splitMarketPath(pathname) {
    var segments = (pathname || '/').split('/');
    if (/^[a-z]{2}-[a-z0-9]+$/i.test(segments[1] || '')) {
      return { prefix: segments[1].toLowerCase(), path: '/' + segments.slice(2).join('/') };
    }
    return { prefix: '', path: pathname || '/' };
  }

  function joinMarketPath(prefix, path) {
    var tail = path && path !== '/' ? path.replace(/\/+$/, '') : '';
    if (prefix) return '/' + prefix + tail;
    return tail || '/';
  }

  // The visitor's query string (UTMs, click ids) and fragment travel with every automatic redirect.
  function carriedQuery() {
    var search = window.location.search || '';
    var hash = window.location.hash || '';
    if (!search || !window.URLSearchParams) return search + hash;
    var params = new URLSearchParams(search);
    var local = [];
    params.forEach(function (value, key) {
      if (LOCAL_PARAMS.test(key)) local.push(key);
    });
    if (!local.length) return search + hash;
    local.forEach(function (key) {
      params.delete(key);
    });
    var query = params.toString();
    return (query ? '?' + query : '') + hash;
  }

  // This page's path on `origin` under the market subfolder `prefix`, read from the page's hreflang
  // alternates - the mapping Google is given. Shopify lists every market/locale of this store (with
  // translated handles), and snippets/cross-store-hreflang.liquid adds the other store with its
  // exceptions (/products/blood-pressure-monitor -> /products/hilo-core) and leaves out pages that
  // have no counterpart. Without a link for that exact subfolder, an English alternate's path is
  // moved under it: Shopify answers /de-eu/pages/about with a 301 to the translated handle.
  // Null when the page has no alternate on that origin.
  function alternatePath(doc, origin, prefix) {
    var links = doc.querySelectorAll('link[rel="alternate"][hreflang][href]');
    var english = null;
    var other = null;
    for (var i = 0; i < links.length; i++) {
      var url;
      try {
        url = new URL(links[i].getAttribute('href'), window.location.href);
      } catch (error) {
        continue;
      }
      if (url.origin !== origin) continue;
      var parts = splitMarketPath(url.pathname);
      if (parts.prefix === prefix) return joinMarketPath(prefix, parts.path);
      var lang = (links[i].getAttribute('hreflang') || '').toLowerCase();
      if (!english && (lang === 'x-default' || lang === 'en' || lang.indexOf('en-') === 0)) english = parts.path;
      if (!other) other = parts.path;
    }
    var path = english || other;
    return path ? joinMarketPath(prefix, path) : null;
  }

  // The cross-store cluster is English only (docs/cross-store-hreflang.md), so a translated page such
  // as /de-eu/pages/unternehmen names no us.hilo.com URL. Its English twin, Shopify's x-default
  // alternate, does. Only a same-origin twin that is a different URL is worth fetching. It is read
  // without cookies: only its hreflang tags are needed, and visiting another market's page with the
  // visitor's cookies could change their saved market or cart.
  function englishTwinUrl() {
    // An English page already carries every cross-store link it can have.
    if (shortLocale(document.documentElement.getAttribute('lang')) === 'en') return null;
    var link = document.querySelector('link[rel="alternate"][hreflang="x-default"][href]');
    if (!link) return null;
    try {
      var url = new URL(link.getAttribute('href'), window.location.href);
      if (url.origin !== window.location.origin) return null;
      if (url.pathname.replace(/\/+$/, '') === window.location.pathname.replace(/\/+$/, '')) return null;
      return url.toString();
    } catch (error) {
      return null;
    }
  }

  function fetchDocument(url) {
    if (!window.fetch || !window.DOMParser) return Promise.reject(new Error('unsupported'));
    var request = fetch(url, { credentials: 'omit', headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('bad response');
        return response.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
    var timeout = new Promise(function (resolve, reject) {
      window.setTimeout(function () {
        reject(new Error('timeout'));
      }, ALTERNATE_FETCH_TIMEOUT);
    });
    return Promise.race([request, timeout]);
  }

  // Where to send a visitor whose store is `target` (an origin, optionally with a market subfolder):
  // this page on that store when it has a counterpart, otherwise that store's home page in the
  // market - or its search page for a search, which hreflang never covers. The query string and
  // fragment are always kept.
  function crossStoreUrl(target) {
    var prefix = splitMarketPath(target.pathname).prefix;
    var fallback = splitMarketPath(window.location.pathname).path.replace(/\/+$/, '') === '/search' ? '/search' : '/';
    var finish = function (path) {
      return target.origin + (path || joinMarketPath(prefix, fallback)) + carriedQuery();
    };
    var path = alternatePath(document, target.origin, prefix);
    var twin = path ? null : englishTwinUrl();
    if (!twin) return Promise.resolve(finish(path));
    return fetchDocument(twin).then(
      function (doc) {
        return finish(alternatePath(doc, target.origin, prefix));
      },
      function () {
        return finish(null);
      }
    );
  }

  // One exit for every automatic navigation. replace() keeps the redirecting page out of the history,
  // so Back from the destination returns to wherever the visitor came from.
  function redirectTo(url) {
    window.location.replace(url);
  }

  function bareHost(hostname) {
    return (hostname || '').toLowerCase().replace(/^www\./, '');
  }

  // Replace one placeholder in a template, writing plain text.
  function fillText(element, template, replacements) {
    if (!element) return;
    var out = template || '';
    for (var token in replacements) {
      if (!Object.prototype.hasOwnProperty.call(replacements, token)) continue;
      out = out.split(token).join(replacements[token] || '');
    }
    element.textContent = out;
  }

  // Same as fillText but the substituted value is wrapped in <strong> (the design emphasises the
  // detected country). Built with DOM nodes so merchant copy is never parsed as HTML.
  function fillTextEmphasised(element, template, token, value, otherReplacements) {
    if (!element) return;
    var out = template || '';
    if (otherReplacements) {
      for (var other in otherReplacements) {
        if (!Object.prototype.hasOwnProperty.call(otherReplacements, other)) continue;
        out = out.split(other).join(otherReplacements[other] || '');
      }
    }
    while (element.firstChild) element.removeChild(element.firstChild);
    var parts = out.split(token);
    for (var i = 0; i < parts.length; i++) {
      if (i > 0) {
        var strong = document.createElement('strong');
        strong.textContent = value || '';
        element.appendChild(strong);
      }
      if (parts[i]) element.appendChild(document.createTextNode(parts[i]));
    }
  }

  function cookieConsentIsVisible() {
    var consent = document.querySelector('.cky-consent-container');
    var notice = consent && consent.querySelector('.cky-consent-bar[data-cky-tag="notice"]');
    if (!notice || notice.classList.contains('cky-hide') || notice.getAttribute('aria-hidden') === 'true') return false;
    var style = window.getComputedStyle ? window.getComputedStyle(notice) : null;
    return !style || (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0');
  }

  // CookieYes is an async app embed and can inject its first-visit banner after the geolocation
  // request resolves. Give it a short load window, then wait for its notice to close before opening
  // our focus-trapped modal. This prevents two stacked dialogs and a hidden focus trap.
  function afterCookieConsent(done) {
    var cookieYesInstalled = !!document.querySelector(
      '.cky-consent-container, script#cookieyes, script[src*="cookieyes" i]'
    );
    if (!cookieYesInstalled || getCookie('cookieyes-consent')) {
      done();
      return;
    }

    var started = Date.now();
    var consentSeen = false;
    var finished = false;
    var observer = null;
    var poll = null;

    function finish() {
      if (finished) return;
      finished = true;
      if (observer) observer.disconnect();
      if (poll) window.clearInterval(poll);
      document.removeEventListener('cookieyes_consent_update', check);
      done();
    }

    function check() {
      if (cookieConsentIsVisible()) {
        consentSeen = true;
        return;
      }
      if (consentSeen || getCookie('cookieyes-consent') || Date.now() - started >= 1800) finish();
    }

    if ('MutationObserver' in window && document.documentElement) {
      observer = new MutationObserver(check);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style', 'aria-hidden'],
        childList: true,
        subtree: true
      });
    }
    document.addEventListener('cookieyes_consent_update', check);
    poll = window.setInterval(check, 100);
    check();
  }

  class GeoBanner extends HTMLElement {
    disconnectedCallback() {
      if (this.isPopup) document.documentElement.classList.remove('geo-popup-is-open');
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      // A prior instance gets relocated to <body> (above the header); when the theme editor
      // re-renders this section it is orphaned outside the section wrapper. Remove any stale
      // duplicates so only this freshly-rendered instance survives.
      var existing = document.querySelectorAll('geo-location-banner');
      for (var i = 0; i < existing.length; i++) {
        if (existing[i] !== this) existing[i].remove();
      }
      try {
        this.init();
      } catch (error) {
        /* fail closed: leave banner hidden */
      }
    }

    init() {
      this.isPopup = this.getAttribute('data-display') === 'popup';
      this.modal = this.querySelector('[data-geo-modal]');

      this.panel = this.querySelector('[data-geo-panel]');
      this.trigger = this.querySelector('[data-geo-trigger]');
      this.triggerFlag = this.querySelector('[data-geo-trigger-flag]');
      this.triggerName = this.querySelector('[data-geo-trigger-name]');
      this.triggerLang = this.querySelector('[data-geo-trigger-lang]');
      this.textEl = this.querySelector('[data-geo-text]');
      this.continueBtn = this.querySelector('[data-geo-continue]');
      this.waitlistBtn = this.querySelector('[data-geo-waitlist]');
      this.closeBtn = this.querySelector('[data-geo-close]');
      this.countryButtons = Array.prototype.slice.call(this.querySelectorAll('.geo-banner__country'));
      this.langAbbrButtons = Array.prototype.slice.call(this.querySelectorAll('.geo-banner__lang-abbr'));
      this.langFullButtons = Array.prototype.slice.call(this.querySelectorAll('.geo-banner__lang-full'));
      this.form = this.querySelector('.shopify-localization-form');
      this.countryInput = this.querySelector('[data-geo-country-input]');
      this.localeInput = this.querySelector('[data-geo-locale-input]');
      this.returnInputs = this.form
        ? Array.prototype.slice.call(this.form.querySelectorAll('[name="return_to"]'))
        : [];

      // Popup-only nodes.
      this.headingEl = this.querySelector('[data-geo-heading]');
      this.bodyEl = this.querySelector('[data-geo-body]');
      this.countryLabelEl = this.querySelector('[data-geo-country-label]');
      this.languageLabelEl = this.querySelector('[data-geo-language-label]');
      this.langTrigger = this.querySelector('[data-geo-lang-trigger]');
      this.langPanel = this.querySelector('[data-geo-lang-panel]');
      this.triggerLangName = this.querySelector('[data-geo-trigger-lang-name]');
      this.stayBtn = this.querySelector('[data-geo-stay]');

      this.defaultLocale = shortLocale(this.getAttribute('data-default-locale')) || 'en';
      this.currentCountry = (this.getAttribute('data-current-country') || '').toUpperCase();
      this.waitlistPath = this.getAttribute('data-waitlist-path') || '/pages/waitlist';
      this.autoRedirect = this.getAttribute('data-auto-redirect') === 'true';
      // First-visit redirect (042b). The countries are empty unless the section setting is on.
      this.redirectCountries = parseList(this.getAttribute('data-auto-redirect-countries'));
      // Countries Shopify can localize this store to. Empty (an older section render) means unknown.
      this.availableCountries = parseList(this.getAttribute('data-available-countries'));
      this.subfolders = parseSubfolders(this.getAttribute('data-market-subfolders'));

      this.strings = this.readStrings();
      this.languages = this.langFullButtons.map(function (btn) {
        return btn.getAttribute('data-locale');
      });

      this.selectedCountry = null;
      this.selectedCountryCode = '';
      this.selectedLocale = this.defaultLocale;
      this.detectedCountryName = '';

      // Name of the market the visitor is currently on, used for [CURRENT MARKET]. The merchant-typed
      // block name (or Shopify's name in the page language) is only the fallback: the visible name is
      // derived from the ISO code in the selected popup language by localizeCountryNames().
      var currentEntry = this.findCountryByGeo(this.currentCountry) || this.findCountryByCode(this.currentCountry);
      this.currentMarketFallback =
        (currentEntry && currentEntry.getAttribute('data-name')) ||
        this.getAttribute('data-current-market-name') ||
        '';
      this.currentMarketName = this.currentMarketFallback;
      this.detectedCountryCode = '';
      this.localizeCountryNames();

      // Dropdown pairs. The banner has a single combined panel; the popup has one per field.
      this.dropdowns = [];
      if (this.trigger && this.panel) this.dropdowns.push({ trigger: this.trigger, panel: this.panel });
      if (this.langTrigger && this.langPanel) this.dropdowns.push({ trigger: this.langTrigger, panel: this.langPanel });

      this.bindEvents();
      if (this.isPopup) this.protectPopupDismissal();
      // Native header/footer localization selector counts as a manual override (runs always).
      this.bindManualOverride();

      if (isDesignMode()) {
        // reveal() relocates this element to <body>. Remember its section so that when the editor
        // later hides or disables the section, the relocated (now orphaned) copy can be removed.
        var sectionEl = this.closest('.shopify-section');
        if (sectionEl && sectionEl.id) this.setAttribute('data-section-id', sectionEl.id);
        // A settings change re-renders the section: this new banner connects and relocates BEFORE
        // Shopify fires shopify:section:load, so flag it as freshly rendered to protect it from the
        // orphan sweep. Cleared on the next microtask, after that synchronous handler has run.
        this._freshRender = true;
        var self = this;
        Promise.resolve().then(function () {
          self._freshRender = false;
        });
        this.presetForDesignMode();
        // The popup is opened by the theme's own handle-editor-events wiring when the merchant
        // selects this section, so it never covers the editor canvas while another section is edited.
        if (!this.isPopup) this.reveal();
        return;
      }

      // snippets/geo-early-redirect.liquid has already sent this visitor to the other store.
      if (window.HiloGeoLeaving) return;

      // Start or end a QA simulation on any page, including those that never run detection below.
      simulatedDetection();

      // Arriving from the other store is a deliberate choice for the rest of the session, whichever
      // page it lands on (the selector page and cart views return before the redirect check).
      if (this.redirectCountries.length && this.cameFromSiblingStore()) setCookie(COOKIE_REDIRECT_GUARD, '1');

      // The country selector page is the deliberate place to choose a market, so it neither prompts
      // nor auto-redirects - otherwise a visitor could never change a choice they had already made.
      if (this.isSelectorPage()) return;

      // A preview QA URL is explicit and repeatable: bypass saved/dismissed cookies and reveal even
      // when the simulated values happen to match the current store. No cookie is modified here.
      var debugDetection = debugDetectionFromUrl();
      if (debugDetection) {
        // Consume QA parameters so Continue and later reloads exercise the normal cookie flow.
        var cleanUrl = new URL(window.location.href);
        ['hilo_geo_country', 'hilo_geo_language', 'hilo_geo_name'].forEach(function (key) { cleanUrl.searchParams.delete(key); });
        window.history.replaceState(window.history.state, '', cleanUrl.toString());
        this.applyDetection(debugDetection);
        this.reveal();
        return;
      }

      // A deliberate choice is read FIRST, because it outranks a dismissal. The two cookies used to be
      // mutually exclusive - a dismissal removed the banner, so no choice could be made afterwards -
      // but the country selector page can now record a choice for a visitor who had already closed the
      // popup. Reading "dismissed" first would strand that visitor on the wrong store until the
      // choice cookie expires or the visitor explicitly changes it.
      // A choice suppresses the prompt on its own (it returns here), so nothing is prompted twice.
      var choice = getCookie(COOKIE_CHOICE);
      // Finish an in-progress manual cross-store choice even when prompting or returning-visitor
      // redirects are disabled. Presentation settings must not prevent Shopify applying the ISO.
      if (choice && this.handleSavedChoice(choice, true)) return;
      // An invalid choice has just been erased; from here on the visitor has none.
      choice = getCookie(COOKIE_CHOICE);

      // A visitor without a saved choice may be redirected on their first page of the session. This
      // is not a prompt, so the device settings do not apply, and closing the popup with X is not a
      // store choice (client decision, 042b), so the dismissed cookie does not block it either.
      var mayRedirect = !choice && this.autoRedirectAllowed();

      if (!this.passesDeviceGate()) {
        if (mayRedirect) this.detectAndShow(true, false);
        return;
      }

      if (choice) {
        this.handleSavedChoice(choice, false);
        return;
      }

      var mayPrompt = !getCookie(COOKIE_DISMISSED);
      if (mayRedirect || mayPrompt) this.detectAndShow(mayRedirect, mayPrompt);
    }

    readStrings() {
      var node = this.querySelector('[data-geo-strings]');
      if (!node) return { fallback: {}, languages: {} };
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return { fallback: {}, languages: {} };
      }
    }

    passesDeviceGate() {
      var isDesktop = window.matchMedia('(min-width: 750px)').matches;
      if (isDesktop && this.getAttribute('data-enable-desktop') !== 'true') return false;
      if (!isDesktop && this.getAttribute('data-enable-mobile') !== 'true') return false;
      return true;
    }

    isSelectorPage() {
      return !!(document.querySelector('hilo-country-selector') || window.HiloGeoSuppressRedirect);
    }

    handleSavedChoice(choice, sameHostOnly) {
      // Choice format: "geo|<destinationUrl>|<countryCode>|<locale>", "waitlist" or "manual" (picked
      // with the theme's own country/language selector). The country and locale were added after
      // launch; two-part cookies remain valid for backward compatibility.
      // The cookie is shared across hilo.com + us.hilo.com, so each store either suppresses the banner
      // (already on the chosen store) or routes the visitor to their chosen store. No crawlers, and never
      // yank a visitor who deliberately navigated here from a sibling store.
      if (isBot()) return true;
      // The country selector page exists so a visitor can change their mind: never bounce them off it.
      if (this.isSelectorPage()) return true;
      var parts = choice.split('|');
      var type = parts[0] || '';
      var rest = parts[1] || '';
      var savedCountry = (parts[2] || '').toUpperCase();
      var savedLocale = shortLocale(parts[3] || '');

      if (type === 'waitlist' || type === 'manual') {
        setCookie(COOKIE_CHOICE, choice, COOKIE_DAYS);
        return true;
      }
      if (type !== 'geo' || !rest) {
        eraseCookie(COOKIE_CHOICE);
        return false;
      }

      var target;
      try {
        target = new URL(rest);
      } catch (error) {
        eraseCookie(COOKIE_CHOICE);
        return false;
      }
      if (!isAllowedDestination(target)) {
        eraseCookie(COOKIE_CHOICE);
        return false;
      }
      // Sliding renewal approximates "until explicitly changed" within browsers' 400-day cap.
      setCookie(COOKIE_CHOICE, choice, COOKIE_DAYS);
      if (!target.hostname) return;

      if (target.hostname === window.location.hostname) {
        // A market subfolder identifies a market group, not the exact country within it. Apply the
        // saved ISO on the destination store through that store's own localization form.
        if (
          /^[A-Z]{2}$/.test(savedCountry) &&
          savedCountry !== this.currentCountry &&
          this.offersCountry(savedCountry) &&
          this.form &&
          this.countryInput &&
          this.localeInput
        ) {
          var alreadyLocalized = false;
          try {
            alreadyLocalized = window.sessionStorage.getItem('hilo_geo_localized') === choice;
          } catch (e) {}
          if (alreadyLocalized) return true;
          try {
            window.sessionStorage.setItem('hilo_geo_localized', choice);
          } catch (e) {}

          this.countryInput.disabled = false;
          this.countryInput.value = savedCountry;
          this.localeInput.disabled = false;
          this.localeInput.value = savedLocale || this.defaultLocale;
          // Stay on this page: Shopify re-localizes the market prefix of return_to, and the visitor
          // may have landed on a product with UTMs rather than on the saved home page.
          this.setReturnTo(window.location.pathname + window.location.search);
          this.form.submit();
          return true;
        }

        // Shopify can redirect the bare root to its primary market. Compare only the locale-market
        // prefix: a DE visitor already on /de-eu/products/... must keep that product path, and one
        // sent to the saved market keeps their page and query string.
        var targetMarket = target.pathname.split('/').filter(Boolean)[0] || '';
        var currentMarket = window.location.pathname.split('/').filter(Boolean)[0] || '';
        if (
          (savedCountry || savedLocale) &&
          /^[a-z]{2}-[a-z0-9]+$/i.test(targetMarket) &&
          targetMarket.toLowerCase() !== currentMarket.toLowerCase()
        ) {
          crossStoreUrl(target).then(redirectTo);
        }
        return true;
      }

      if (sameHostOnly) return false;
      if (!this.autoRedirect || !this.isRedirectablePage()) return true;

      // Respect a deliberate hop between sibling stores: if the visitor arrived via a link from a
      // hilo.com-family host, leave them where they chose to go.
      var brand = window.location.hostname.split('.').slice(-2).join('.');
      var refHost = '';
      try {
        refHost = document.referrer ? new URL(document.referrer).hostname : '';
      } catch (e) {}
      if (refHost && new RegExp('(^|\\.)' + brand.replace(/\./g, '\\.') + '$').test(refHost)) return true;

      // Loop safety: redirect at most once per browsing session.
      var bounced = false;
      try {
        bounced = window.sessionStorage.getItem('hilo_geo_bounced') === choice;
      } catch (e) {}
      if (bounced) return true;
      try {
        window.sessionStorage.setItem('hilo_geo_bounced', choice);
      } catch (e) {}

      // The same page on the chosen store, with its query string - not that store's home page.
      crossStoreUrl(target).then(redirectTo);
      return true;
    }

    // Everything about the first-visit redirect that is known before the country lookup returns.
    // The server-side rules (setting on, not a cart/account/password page, the optional cart and
    // login exemption) arrive as data-auto-redirect-eligible.
    autoRedirectAllowed() {
      if (!this.redirectCountries.length || this.getAttribute('data-auto-redirect-eligible') !== 'true') return false;
      if (isBot() || !this.isRedirectablePage()) return false;
      // The sibling check repeats the guard for browsers that refuse the cookie.
      if (getCookie(COOKIE_REDIRECT_GUARD) || this.cameFromSiblingStore()) return false;
      return true;
    }

    // Pages no automatic redirect may leave, first-visit or returning: the server's page-type rules
    // (data-storefront-page), cart/checkout/account/app-proxy paths and the theme's cart view.
    isRedirectablePage() {
      if (this.getAttribute('data-storefront-page') === 'false') return false;
      return !NO_REDIRECT_PATH.test(window.location.pathname || '/') && !window.HiloGeoCartView;
    }

    // The other Hilo store: any host a country entry sends visitors to (us.hilo.com on hilo.com).
    // Narrower than "any *.hilo.com host" on purpose - a help-centre link is not a store choice.
    cameFromSiblingStore() {
      var refHost = '';
      try {
        refHost = document.referrer ? bareHost(new URL(document.referrer).hostname) : '';
      } catch (error) {}
      if (!refHost || refHost === bareHost(window.location.hostname)) return false;
      for (var i = 0; i < this.countryButtons.length; i++) {
        try {
          var external = (this.countryButtons[i].getAttribute('data-external-url') || '').trim();
          if (external && bareHost(new URL(external).hostname) === refHost) return true;
        } catch (error) {}
      }
      return false;
    }

    // Whether Shopify can localize this store to `code`. Territories such as PR are detected as the
    // US but are not countries of the us.hilo.com market, so re-applying them would submit the form
    // on every new session without effect.
    offersCountry(code) {
      return !this.availableCountries.length || this.availableCountries.indexOf(code.toLowerCase()) !== -1;
    }

    // Send a visitor detected in one of the configured countries to the store of the country entry
    // that serves it (its External store URL). Returns false when this visitor is not redirected.
    autoRedirectTo(data) {
      var detected = data && data.detected_values && data.detected_values.country;
      var code = ((detected && detected.handle) || '').toUpperCase();
      if (!/^[A-Z]{2}$/.test(code) || this.redirectCountries.indexOf(code.toLowerCase()) === -1) return false;
      var entry = this.findCountryByGeo(code);
      var target;
      try {
        target = new URL((entry && entry.getAttribute('data-external-url')) || '');
      } catch (error) {
        return false;
      }
      if (!isAllowedDestination(target) || target.hostname === window.location.hostname) return false;
      // Set before leaving, so coming back to this store in the same session never redirects again.
      setCookie(COOKIE_REDIRECT_GUARD, '1');
      crossStoreUrl(target).then(redirectTo);
      return true;
    }

    detectAndShow(mayRedirect, mayPrompt) {
      var self = this;
      this.fetchSuggestions()
        .then(null, function () {
          return null;
        })
        .then(function (data) {
          if (mayRedirect && self.autoRedirectTo(data)) return;
          if (mayPrompt && self.applyDetection(data)) self.reveal();
        });
    }

    fetchSuggestions() {
      var simulated = simulatedDetection();
      if (simulated) return Promise.resolve(simulated);
      if (window.HiloGeoBannerSuggestions && typeof window.HiloGeoBannerSuggestions.then === 'function') {
        return window.HiloGeoBannerSuggestions;
      }
      return fetch(SUGGESTIONS_URL, { headers: { Accept: 'application/json' } }).then(function (response) {
        if (!response.ok) throw new Error('bad response');
        return response.json();
      });
    }

    applyDetection(data) {
      var detectedCountry = '';
      var detectedLocale = '';

      if (data && data.detected_values) {
        if (data.detected_values.country && data.detected_values.country.handle) {
          detectedCountry = data.detected_values.country.handle.toUpperCase();
        }
        // Shopify returns the country name already localized for the current storefront locale.
        this.detectedCountryName =
          (data.detected_values.country && data.detected_values.country.name) ||
          data.detected_values.country_name ||
          '';
        if (data.detected_values.language && data.detected_values.language.handle) {
          detectedLocale = shortLocale(data.detected_values.language.handle);
        }
      }

      this.detectedCountryCode = detectedCountry;

      // Browser language is the signal for requirement B ("a different browser language than the
      // country he is in"); it also seeds language detection when Shopify returns none.
      var browserLang = shortLocale(navigator.language || navigator.userLanguage || '');
      if (!detectedLocale) detectedLocale = browserLang;

      // Match a configured country by its geo codes, then fall back to a wildcard ("*") catch-all
      // entry if one is configured (used on the US store to route any non-US visitor to hilo.com).
      var matched = null;
      if (detectedCountry) {
        matched = this.findCountryByGeo(detectedCountry) || this.findCatchAll();
      }

      if (matched && matched.getAttribute('data-waitlist') === 'true') {
        // A catch-all entry the merchant has flagged as "we do not sell here yet". Without this flag a
        // wildcard entry would look like a real market and offer "Continue to <that entry>".
        this.setMode('waitlist');
        this.selectLocale(this.pickLocale(this.localeForCountry(matched, detectedLocale)), false);
      } else if (matched) {
        this.setMode('country');
        this.selectCountry(matched, this.localeForCountry(matched, detectedLocale), detectedCountry);
      } else if (detectedCountry) {
        // Detected, but not a selling market -> waiting list.
        this.setMode('waitlist');
        this.selectLocale(this.pickLocale(detectedLocale), false);
      } else {
        // Detection unavailable -> best-effort supported view.
        this.setMode('country');
        var fallbackCountry = this.findCountryByCode(this.currentCountry) || this.countryButtons[0];
        if (fallbackCountry) {
          this.selectCountry(fallbackCountry, this.localeForCountry(fallbackCountry, detectedLocale));
        }
      }

      return this.shouldReveal(detectedCountry, matched, browserLang);
    }

    // Pick the locale to pre-select for a country: the visitor's language when that market offers
    // it, otherwise English (the agreed fallback, most widely understood), otherwise the market's
    // own default language (requirement B fallback).
    localeForCountry(button, preferred) {
      var langs = parseList(button.getAttribute('data-langs'));
      if (preferred && this.languages.indexOf(preferred) !== -1 && (langs.length === 0 || langs.indexOf(preferred) !== -1)) {
        return preferred;
      }
      if (langs.indexOf('en') !== -1 || (langs.length === 0 && this.languages.indexOf('en') !== -1)) {
        return 'en';
      }
      return shortLocale(button.getAttribute('data-default-lang')) || langs[0] || this.defaultLocale;
    }

    // Requirement A + B: only show the banner when the visitor's context does not already match the
    // store they landed on. Suppress when BOTH their detected country is served by this store/market
    // (A) AND their browser language matches the language currently shown (B). Any mismatch shows it.
    shouldReveal(detectedCountry, matched, browserLang) {
      var countryMatches;
      if (matched && matched.getAttribute('data-waitlist') === 'true') {
        // Matched an entry that means "not a market we serve": always worth telling the visitor,
        // whatever the store's own current-country context happens to be.
        countryMatches = false;
      } else if (!detectedCountry) {
        // Detection unavailable: we cannot assert a country mismatch, so let language decide.
        countryMatches = true;
      } else {
        var currentEntry = this.findCountryByGeo(this.currentCountry) || this.findCountryByCode(this.currentCountry);
        var external = matched ? (matched.getAttribute('data-external-url') || '').trim() : '';
        if (currentEntry) {
          countryMatches = !!matched && matched === currentEntry && !external;
        } else {
          // The current market maps to no configured country block, or the store gave no
          // current-country context (blank iso_code). Treat it as a match when the detected country
          // is served here locally, so a correctly-placed visitor is not shown the banner.
          countryMatches = !!matched && !external && (!this.currentCountry || detectedCountry === this.currentCountry);
        }
      }

      // A language mismatch only counts when we actually publish the browser's language; otherwise
      // the English/market-language fallback is the best we can offer and there is nothing to switch.
      var storeLanguages = this.storeLanguages();
      var languageMismatch =
        !!browserLang &&
        this.languages.indexOf(browserLang) !== -1 &&
        (!storeLanguages || storeLanguages.indexOf(browserLang) !== -1) &&
        browserLang !== this.defaultLocale;

      return !(countryMatches && !languageMismatch);
    }

    // Languages this store sells in: those of its own markets, i.e. country entries without an External
    // store URL. The language blocks are not it - they translate the popup for every visitor, so
    // us.hilo.com has five but publishes English only. Null means no restriction (an own entry that
    // allows every language, or none configured). Liquid offers no store-wide list:
    // shop.published_locales only covers the current market.
    storeLanguages() {
      var langs = [];
      for (var i = 0; i < this.countryButtons.length; i++) {
        var btn = this.countryButtons[i];
        if ((btn.getAttribute('data-external-url') || '').trim() || btn.getAttribute('data-waitlist') === 'true') continue;
        var own = parseList(btn.getAttribute('data-langs'));
        if (!own.length) return null;
        own.forEach(function (lang) {
          if (langs.indexOf(lang) === -1) langs.push(lang);
        });
      }
      return langs.length ? langs : null;
    }

    pickLocale(preferred) {
      if (preferred && this.languages.indexOf(preferred) !== -1) return preferred;
      if (this.languages.indexOf('en') !== -1) return 'en';
      if (this.languages.indexOf(this.defaultLocale) !== -1) return this.defaultLocale;
      return this.languages[0] || this.defaultLocale;
    }

    findCountryByGeo(code) {
      if (!code) return null;
      for (var i = 0; i < this.countryButtons.length; i++) {
        var geo = parseList(this.countryButtons[i].getAttribute('data-geo-codes'));
        if (geo.indexOf(code.toLowerCase()) !== -1) return this.countryButtons[i];
      }
      return null;
    }

    findCountryByCode(code) {
      if (!code) return null;
      for (var i = 0; i < this.countryButtons.length; i++) {
        if ((this.countryButtons[i].getAttribute('data-country-code') || '').toUpperCase() === code.toUpperCase()) {
          return this.countryButtons[i];
        }
      }
      return null;
    }

    findCatchAll() {
      for (var i = 0; i < this.countryButtons.length; i++) {
        if (parseList(this.countryButtons[i].getAttribute('data-geo-codes')).indexOf('*') !== -1) {
          return this.countryButtons[i];
        }
      }
      return null;
    }

    setMode(mode) {
      this.classList.remove('geo-banner--country', 'geo-banner--waitlist');
      this.classList.add('geo-banner--' + mode);
      this.setAttribute('data-mode', mode);
      this.mode = mode;
    }

    selectCountry(button, locale, countryCodeOverride) {
      if (!button) return;
      this.selectedCountry = button;
      var configuredCode = (button.getAttribute('data-country-code') || '').toUpperCase();
      var exactCode = (countryCodeOverride || '').toUpperCase();
      var mappedCodes = parseList(button.getAttribute('data-geo-codes'));
      this.selectedCountryCode =
        /^[A-Z]{2}$/.test(exactCode) && mappedCodes.indexOf(exactCode.toLowerCase()) !== -1
          ? exactCode
          : configuredCode;
      this.countryButtons.forEach(function (btn) {
        var selected = btn === button ? 'true' : 'false';
        btn.setAttribute('aria-pressed', selected);
      });

      var langs = parseList(button.getAttribute('data-langs'));
      // Enable/disable the language options based on the country.
      this.langAbbrButtons.forEach(function (btn) {
        var loc = btn.getAttribute('data-locale');
        var allowed = langs.length === 0 || langs.indexOf(loc) !== -1;
        if (allowed) {
          btn.removeAttribute('disabled');
        } else {
          btn.setAttribute('disabled', 'disabled');
        }
      });
      this.langFullButtons.forEach(function (btn) {
        var loc = btn.getAttribute('data-locale');
        var allowed = langs.length === 0 || langs.indexOf(loc) !== -1;
        if (allowed) {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.setAttribute('disabled', 'disabled');
          btn.setAttribute('aria-disabled', 'true');
        }
      });

      // Ensure the selected locale is valid for this country.
      var nextLocale = locale;
      if (langs.length && langs.indexOf(nextLocale) === -1) {
        nextLocale = shortLocale(button.getAttribute('data-default-lang')) || langs[0];
      }
      this.selectLocale(nextLocale, false);

      // Trigger flag + name.
      var flag = button.querySelector('.geo-banner__flag');
      if (this.triggerFlag) this.triggerFlag.innerHTML = flag ? flag.outerHTML : '';
      if (this.triggerName) this.triggerName.textContent = this.displayNameFor(button);

      this.updateTexts();
    }

    selectLocale(locale, closePanel) {
      this.selectedLocale = locale;
      // Assistive technology must pronounce the translated popup/banner in its own language.
      this.setAttribute('lang', locale);
      this.localizeCountryNames();

      this.langAbbrButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-locale') === locale ? 'true' : 'false');
      });
      this.langFullButtons.forEach(function (btn) {
        var selected = btn.getAttribute('data-locale') === locale ? 'true' : 'false';
        btn.setAttribute('aria-pressed', selected);
      });

      var label = this.strings.languages && this.strings.languages[locale] && this.strings.languages[locale].label;
      if (this.triggerLang) this.triggerLang.textContent = label || locale.toUpperCase();

      if (this.triggerLangName) {
        var selected = null;
        for (var i = 0; i < this.langFullButtons.length; i++) {
          if (this.langFullButtons[i].getAttribute('data-locale') === locale) {
            selected = this.langFullButtons[i];
            break;
          }
        }
        var name = selected && selected.getAttribute('data-lang-name');
        if (!name) {
          var langStrings = this.strings.languages && this.strings.languages[locale];
          name = (langStrings && (langStrings.name || langStrings.label)) || locale.toUpperCase();
        }
        this.triggerLangName.textContent = name;
      }

      this.updateTexts();

      if (closePanel) this.closePanelsAndRefocus();
    }

    // Show every country in the selected popup language. Entries without an ISO code (the
    // rest-of-world catch-all) take the language block's own label, then the merchant-typed name.
    localizeCountryNames() {
      var locale = this.selectedLocale || this.defaultLocale;
      var langStrings = (this.strings.languages && this.strings.languages[locale]) || {};
      this.countryButtons.forEach(function (btn) {
        var code = (btn.getAttribute('data-country-code') || '').trim();
        var fallback = btn.getAttribute('data-name') || '';
        var name = code ? countryDisplayName(code, locale, fallback) : langStrings.catchAllName || fallback;
        btn.setAttribute('data-display-name', name);
        var label = btn.querySelector('.geo-banner__country-name');
        if (label) label.textContent = name;
      });
      this.currentMarketName = countryDisplayName(this.currentCountry, locale, this.currentMarketFallback);
      if (this.triggerName && this.selectedCountry) this.triggerName.textContent = this.displayNameFor(this.selectedCountry);
    }

    displayNameFor(button) {
      if (!button) return '';
      return button.getAttribute('data-display-name') || button.getAttribute('data-name') || '';
    }

    getStrings(locale) {
      var langStrings = (this.strings.languages && this.strings.languages[locale]) || {};
      var fallback = this.strings.fallback || {};
      function pick(key) {
        return langStrings[key] || fallback[key] || '';
      }
      return {
        heading: pick('heading'),
        continue: pick('continue'),
        waitlistHeading: pick('waitlistHeading'),
        waitlistButton: pick('waitlistButton'),
        waitlistUrl: pick('waitlistUrl'),
        popupHeading: pick('popupHeading'),
        popupBody: pick('popupBody'),
        popupContinue: pick('popupContinue'),
        popupStay: pick('popupStay'),
        popupWaitlistHeading: pick('popupWaitlistHeading'),
        countryLabel: pick('countryLabel'),
        languageLabel: pick('languageLabel')
      };
    }

    updateTexts() {
      var s = this.getStrings(this.selectedLocale);

      if (this.textEl) this.textEl.textContent = this.mode === 'waitlist' ? s.waitlistHeading : s.heading;
      if (this.continueBtn && !this.isPopup) this.continueBtn.textContent = s.continue;
      if (this.waitlistBtn) this.waitlistBtn.textContent = s.waitlistButton;

      if (this.isPopup) this.updatePopupTexts(s);
    }

    updatePopupTexts(s) {
      var recommended = this.selectedCountry ? this.displayNameFor(this.selectedCountry) : '';
      // Name the detected country in the popup language as well, so heading, body, dropdown and
      // button agree. Shopify's name (page language) is the fallback, then the recommended market.
      var detected = countryDisplayName(this.detectedCountryCode, this.selectedLocale, this.detectedCountryName || recommended);
      var isWaitlist = this.mode === 'waitlist';

      var tokens = {};
      tokens[TOKEN_CURRENT] = this.currentMarketName;
      tokens[TOKEN_RECOMMENDED] = recommended;
      tokens[TOKEN_DETECTED] = detected;

      if (this.headingEl) {
        fillText(this.headingEl, isWaitlist ? s.popupWaitlistHeading : s.popupHeading, tokens);
      }

      if (this.bodyEl) {
        if (isWaitlist) {
          fillText(this.bodyEl, s.waitlistHeading, tokens);
        } else {
          var others = {};
          others[TOKEN_CURRENT] = this.currentMarketName;
          others[TOKEN_RECOMMENDED] = recommended;
          fillTextEmphasised(this.bodyEl, s.popupBody, TOKEN_DETECTED, detected, others);
        }
      }

      fillText(this.continueBtn, s.popupContinue, tokens);
      fillText(this.stayBtn, s.popupStay, tokens);
      if (this.countryLabelEl && s.countryLabel) this.countryLabelEl.textContent = s.countryLabel;
      if (this.languageLabelEl && s.languageLabel) this.languageLabelEl.textContent = s.languageLabel;
    }

    presetForDesignMode() {
      this.setMode('country');
      var country = this.findCountryByCode(this.currentCountry) || this.countryButtons[0];
      if (country) {
        var langs = parseList(country.getAttribute('data-langs'));
        var locale = langs.indexOf(this.defaultLocale) !== -1 ? this.defaultLocale : langs[0] || this.defaultLocale;
        this.selectCountry(country, locale);
      }
    }

    bindEvents() {
      var self = this;

      this.dropdowns.forEach(function (entry) {
        entry.trigger.addEventListener('click', function () {
          self.togglePanel(entry);
        });
      });

      this.countryButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          // Manually picking a country preselects that country's default language (req 17).
          self.selectCountry(btn, shortLocale(btn.getAttribute('data-default-lang')));
          if (self.isPopup) self.closePanelsAndRefocus();
        });
      });

      this.langAbbrButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.hasAttribute('disabled')) return;
          self.selectLocale(btn.getAttribute('data-locale'), false);
        });
      });

      this.langFullButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.hasAttribute('disabled')) return;
          self.selectLocale(btn.getAttribute('data-locale'), true);
        });
      });

      if (this.continueBtn) {
        this.continueBtn.addEventListener('click', function (event) {
          event.preventDefault();
          self.onContinue();
        });
      }

      if (this.waitlistBtn) {
        this.waitlistBtn.addEventListener('click', function (event) {
          event.preventDefault();
          self.onWaitlist();
        });
      }

      if (this.stayBtn) {
        this.stayBtn.addEventListener('click', function (event) {
          event.preventDefault();
          self.onStay();
        });
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', function () {
          self.onDismiss();
        });
      }

      if (this.modal) {
        // An approved close from the popup X or an in-popup action counts as a dismissal, unless
        // the visitor made a choice - those write the choice cookie instead, which must not be
        // shadowed by the dismissed cookie.
        this.modal.addEventListener('dialog:before-hide', function () {
          document.documentElement.classList.remove('geo-popup-is-open');
          self.closeAllPanels();
          if (isDesignMode() || self._actedOnChoice) return;
          setCookie(COOKIE_DISMISSED, '1', COOKIE_DAYS);
        });
      }

      document.addEventListener('click', function (event) {
        var open = self.openDropdown();
        if (!open) return;
        // Close when the click is anywhere outside the trigger and the panel itself.
        var t = event.target;
        if (open.trigger.contains(t) || open.panel.contains(t)) return;
        self.closeAllPanels();
      });

      // Re-fit an open popup menu after an orientation or viewport-size change. This keeps the
      // options reachable without making the normal-height Figma modal grow into a scroll sheet.
      window.addEventListener('resize', function () {
        var open = self.openDropdown();
        if (open) self.fitPanelToViewport(open);
      });

      // Escape closes an open dropdown and nothing else. Registered in the capture phase during init,
      // i.e. before the modal's focus trap binds its own document listener, so the popup is not torn
      // down (and silently recorded as dismissed) just because a dropdown was open.
      document.addEventListener(
        'keydown',
        function (event) {
          if (event.key !== 'Escape') return;
          var open = self.openDropdown();
          if (!open) return;
          event.stopImmediatePropagation();
          event.preventDefault();
          self.closePanelsAndRefocus();
        },
        true
      );
    }

    protectPopupDismissal() {
      var modal = this.modal;
      if (!modal || isDesignMode()) return;

      function bind() {
        var overlay = modal.shadowRoot && modal.shadowRoot.querySelector('[part="overlay"]');
        if (!overlay || overlay.__hiloGeoBackdropGuard || modal.__hiloGeoOriginalHide) return;
        overlay.__hiloGeoBackdropGuard = true;
        modal.__hiloGeoOriginalHide = modal.hide.bind(modal);

        // Ignore every unapproved hide() call. This covers the backdrop, the theme focus trap and
        // external overlays such as CookieYes. Only the modal X (dialog:force-close) and our own
        // action methods set the one-shot authorization flag.
        modal.hide = function () {
          if (!modal.__hiloGeoAllowHide) return;
          modal.__hiloGeoAllowHide = false;
          return modal.__hiloGeoOriginalHide.apply(modal, arguments);
        };
        modal.addEventListener(
          'dialog:force-close',
          function () {
            modal.__hiloGeoAllowHide = true;
          },
          true
        );

        // The theme also binds overlay.click -> hide(). Stop that event before the theme listener.
        overlay.addEventListener(
          'click',
          function (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
          },
          true
        );
      }

      if (window.customElements && !window.customElements.get('x-modal') && window.customElements.whenDefined) {
        window.customElements.whenDefined('x-modal').then(bind);
      } else {
        bind();
      }
    }

    bindManualOverride() {
      // Using the theme's native header/footer country/language selector is an explicit manual
      // choice that must override geo detection: stop auto-redirecting and don't show the banner again.
      var self = this;
      var forms = Array.prototype.slice.call(document.querySelectorAll('form.shopify-localization-form'));
      forms.forEach(function (form) {
        if (self.contains(form)) return; // ignore the banner's own form
        // The country selector page records a deliberate choice of its own; submitting its form must
        // not erase the cookie it has just written.
        if (form.closest && form.closest('hilo-country-selector')) return;
        if (form.hasAttribute('data-geo-choice-form')) return;
        form.addEventListener('submit', function () {
          // A store choice in its own right, so it also blocks the first-visit redirect - which a
          // dismissal no longer does. "manual" never routes anywhere: Shopify applies the selection.
          setCookie(COOKIE_CHOICE, 'manual', COOKIE_DAYS);
          setCookie(COOKIE_DISMISSED, '1', COOKIE_DAYS);
          // A native market change invalidates both one-hop guards. Clearing them allows a visitor
          // to choose the same prior country again from the dedicated selector in this tab.
          try {
            window.sessionStorage.removeItem('hilo_geo_localized');
            window.sessionStorage.removeItem('hilo_geo_bounced');
          } catch (e) {}
        });
      });
    }

    openDropdown() {
      for (var i = 0; i < this.dropdowns.length; i++) {
        if (this.dropdowns[i].open) return this.dropdowns[i];
      }
      return null;
    }

    togglePanel(entry) {
      if (entry.open) {
        this.closeAllPanels();
      } else {
        this.openPanel(entry);
      }
    }

    openPanel(entry) {
      this.closeAllPanels();
      entry.panel.hidden = false;
      entry.open = true;
      entry.trigger.setAttribute('aria-expanded', 'true');
      this.fitPanelToViewport(entry);
    }

    fitPanelToViewport(entry) {
      entry.panel.style.removeProperty('max-height');
      if (!window.matchMedia || !window.matchMedia('(min-height: 700px)').matches) return;

      var panelTop = entry.panel.getBoundingClientRect().top;
      var available = Math.floor(window.innerHeight - panelTop - 20);
      // The normal design is 264px tall. When the trigger sits lower in the viewport, cap only the
      // outer panel and let its existing overflow scroll the options instead of clipping the menu.
      entry.panel.style.maxHeight = Math.max(72, Math.min(264, available)) + 'px';
    }

    closeAllPanels() {
      this.dropdowns.forEach(function (entry) {
        entry.panel.hidden = true;
        entry.panel.style.removeProperty('max-height');
        entry.open = false;
        entry.trigger.setAttribute('aria-expanded', 'false');
      });
    }

    // Hiding a panel while it still holds focus would drop focus to <body>, outside the dialog. Used
    // whenever the panel is closed by a deliberate action (Escape, choosing an option).
    closePanelsAndRefocus() {
      var open = this.openDropdown();
      var trigger = open && open.trigger;
      var hadFocus = trigger && open.panel.contains(document.activeElement);
      this.closeAllPanels();
      if (trigger && (hadFocus || document.activeElement === document.body)) trigger.focus();
    }

    setReturnTo(path) {
      this.returnInputs.forEach(function (input) {
        input.value = path;
      });
    }

    // Market subfolder for a country code, e.g. "eu" for DE. Empty when the store publishes its
    // markets at the root (single-market stores).
    subfolderFor(code) {
      if (!code) return '';
      return this.subfolders[code.toString().toLowerCase()] || '';
    }

    onContinue() {
      if (isDesignMode()) return;
      var country = this.selectedCountry;
      if (!country) return;

      var externalUrl = country.getAttribute('data-external-url');
      var code = this.selectedCountryCode || (country.getAttribute('data-country-code') || '').toUpperCase();

      if (externalUrl) {
        // Forward to the external store's homepage in the visitor's selected language, in the
        // subfolder that store publishes the target market under. An entry with no country code maps
        // to no market, so its URL is used verbatim - prefixing it would invent a path that 404s.
        var dest = code ? withLocalePrefix(externalUrl, this.selectedLocale, this.subfolderFor(code)) : externalUrl;
        this._actedOnChoice = true;
        setCookie(COOKIE_CHOICE, 'geo|' + dest + '|' + code + '|' + this.selectedLocale, COOKIE_DAYS);
        window.location.href = dest;
        return;
      }

      if (this.form && this.countryInput && this.localeInput) {
        // Record the chosen store (this store) + locale so a sibling store can route back here.
        this._actedOnChoice = true;
        setCookie(
          COOKIE_CHOICE,
          'geo|' +
            withLocalePrefix(window.location.origin, this.selectedLocale, this.subfolderFor(code)) +
            '|' +
            code +
            '|' +
            this.selectedLocale,
          COOKIE_DAYS
        );
        this.countryInput.disabled = false;
        this.countryInput.value = code;
        this.localeInput.disabled = false;
        this.localeInput.value = this.selectedLocale;
        // Return to the exact current URL (path + query string); Shopify re-localizes the
        // locale prefix when locale_code changes, so the visitor keeps their page state.
        this.setReturnTo(window.location.pathname + window.location.search);
        this.form.submit();
      }
    }

    // "Stay on <current market> store": record this store/market/locale as the deliberate choice so
    // the visitor is never prompted again, and close the popup without navigating.
    onStay() {
      if (isDesignMode()) return;
      this._actedOnChoice = true;
      setCookie(
        COOKIE_CHOICE,
        'geo|' +
          withLocalePrefix(window.location.origin, this.defaultLocale, this.subfolderFor(this.currentCountry)) +
          '|' +
          this.currentCountry +
          '|' +
          this.defaultLocale,
        COOKIE_DAYS
      );
      this.hide();
    }

    onWaitlist() {
      if (isDesignMode()) return;
      this._actedOnChoice = true;
      setCookie(COOKIE_CHOICE, 'waitlist', COOKIE_DAYS);
      var localizedWaitlistPath = this.getStrings(this.selectedLocale).waitlistUrl || this.waitlistPath;

      try {
        var waitlistUrl = new URL(localizedWaitlistPath, window.location.origin);
        if (waitlistUrl.origin !== window.location.origin) {
          window.location.href = waitlistUrl.toString();
          return;
        }
      } catch (error) {}

      if (this.form && this.localeInput) {
        if (this.countryInput) this.countryInput.disabled = true;
        this.localeInput.disabled = false;
        this.localeInput.value = this.selectedLocale;
        this.setReturnTo(localizedWaitlistPath);
        this.form.submit();
      } else {
        window.location.href = localizedWaitlistPath;
      }
    }

    onDismiss() {
      setCookie(COOKIE_DISMISSED, '1', COOKIE_DAYS);
      this.remove();
    }

    hide() {
      if (this.modal && typeof this.modal.hide === 'function') {
        this.modal.__hiloGeoAllowHide = true;
        this.modal.hide();
        return;
      }
      this.hidden = true;
    }

    // Lock each action button to the width of its widest language variant so switching language
    // never reflows the row (which would shift the open dropdown).
    lockButtonWidths() {
      this.lockWidth(this.continueBtn, this.collectStrings('continue'));
      this.lockWidth(this.waitlistBtn, this.collectStrings('waitlistButton'));
    }

    collectStrings(key) {
      var out = [];
      var langs = (this.strings && this.strings.languages) || {};
      Object.keys(langs).forEach(function (k) {
        if (langs[k] && langs[k][key]) out.push(langs[k][key]);
      });
      var fallback = (this.strings && this.strings.fallback) || {};
      if (fallback[key]) out.push(fallback[key]);
      return out;
    }

    lockWidth(btn, strings) {
      if (!btn || !strings.length) return;
      var cs = window.getComputedStyle(btn);
      var probe = document.createElement('span');
      probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;';
      probe.style.fontFamily = cs.fontFamily;
      probe.style.fontSize = cs.fontSize;
      probe.style.fontWeight = cs.fontWeight;
      probe.style.fontStyle = cs.fontStyle;
      probe.style.letterSpacing = cs.letterSpacing;
      probe.style.textTransform = cs.textTransform;
      document.body.appendChild(probe);
      var max = 0;
      for (var i = 0; i < strings.length; i++) {
        probe.textContent = strings[i];
        if (probe.offsetWidth > max) max = probe.offsetWidth;
      }
      document.body.removeChild(probe);
      var extra =
        parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
      btn.style.minWidth = Math.ceil(max + extra + 2) + 'px';
    }

    reveal() {
      if (this.isPopup) {
        this.revealPopup();
        return;
      }
      // Move to the very top of the page so it sits above the header, then show.
      if (document.body && this.parentNode !== document.body) {
        document.body.insertBefore(this, document.body.firstChild);
      }
      this.hidden = false;
      this.lockButtonWidths();
    }

    // The popup is a full-page interstitial, so it is never shown to crawlers. <x-modal> is defined
    // by the theme bundle; wait for the upgrade so show() exists before calling it.
    revealPopup() {
      var modal = this.modal;
      if (!modal || isBot()) return;
      var self = this;
      var show = function () {
        if (!self.isConnected) return;
        document.documentElement.classList.add('geo-popup-is-open');
        if (typeof modal.show === 'function') {
          modal.show();
        } else {
          modal.setAttribute('open', '');
        }
      };
      afterCookieConsent(function () {
        if (window.customElements && !window.customElements.get('x-modal') && window.customElements.whenDefined) {
          window.customElements.whenDefined('x-modal').then(show);
        } else {
          show();
        }
      });
    }
  }

  // Theme editor only. Because the banner is relocated to <body>, hiding or disabling its section
  // would otherwise leave the moved copy on screen: the section re-renders empty (or unloads) but the
  // orphan lingers. When a section (re)loads or unloads with no banner inside it, drop any banner we
  // relocated out of that section (matched by the id captured before relocation).
  function removeOrphanedBanner(sectionEl) {
    if (!sectionEl || !sectionEl.id) return;
    // If the section (re)rendered a banner inside itself, nothing was orphaned.
    if (sectionEl.querySelector && sectionEl.querySelector('geo-location-banner')) return;
    var banners = document.querySelectorAll('geo-location-banner');
    for (var i = 0; i < banners.length; i++) {
      var banner = banners[i];
      // A just-rendered banner relocates to <body> before section:load fires; only remove a copy
      // from this section that was NOT freshly rendered (i.e. a leftover from a disabled/hidden one).
      if (banner.getAttribute('data-section-id') === sectionEl.id && !banner._freshRender) banner.remove();
    }
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:unload', function (event) {
      removeOrphanedBanner(event.target);
    });
    document.addEventListener('shopify:section:load', function (event) {
      removeOrphanedBanner(event.target);
    });
  }

  if ('customElements' in window && !window.customElements.get('geo-location-banner')) {
    window.customElements.define('geo-location-banner', GeoBanner);
  }
})();
